import React, { createContext, use, useState, useCallback, useMemo, ReactNode } from 'react';

interface IdentityRefusedContextValue {
  /**
   * Connection provider of the identity that already holds this email (e.g.
   * `google-oauth2`, `auth0`), or null when no refusal has been signaled.
   * Set from the api-gateway's `X-Identity-Refused` response header (the
   * deny-only identity policy, WOL-393/WOL-471) at the fetchWithAuth
   * chokepoint.
   */
  refusedConnection: string | null;
  triggerIdentityRefused: (connection: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const IdentityRefusedContext = createContext<IdentityRefusedContextValue | null>(null);

export const IdentityRefusedProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [refusedConnection, setRefusedConnection] = useState<string | null>(null);
  const triggerIdentityRefused = useCallback(
    (connection: string) => setRefusedConnection(connection),
    [],
  );
  const value = useMemo(
    () => ({ refusedConnection, triggerIdentityRefused }),
    [refusedConnection, triggerIdentityRefused],
  );

  return (
    <IdentityRefusedContext value={value}>
      {children}
    </IdentityRefusedContext>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useIdentityRefused = (): IdentityRefusedContextValue => {
  const ctx = use(IdentityRefusedContext);
  if (!ctx) {
    throw new Error('useIdentityRefused must be used within an IdentityRefusedProvider');
  }
  return ctx;
};
